// SPDX-License-Identifier: AGPL-3.0-or-later

import {ChannelHeaderIcon} from '@app/features/channel/components/channel_header_components/ChannelHeaderIcon';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import {CompanionVoicePreview} from '@app/features/companion/components/CompanionVoicePreview';
import CompanionSessionStore from '@app/features/companion/state/CompanionSessionStore';
import CompanionTalkPreferences from '@app/features/companion/state/CompanionTalkPreferences';
import styles from '@app/features/discovery/discovery/DiscoveryCompanionTalkPage.module.css';
import {GO_BACK_DESCRIPTOR, TRY_AGAIN_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Navigation from '@app/features/navigation/state/Navigation';
import {Button} from '@app/features/ui/button/Button';
import {Scroller} from '@app/features/ui/components/Scroller';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {getFormattedCompactDateTime} from '@app/features/user/utils/DateFormatting';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArrowLeftIcon, ChatsCircleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useState} from 'react';

const AI_CHAT_TITLE_DESCRIPTOR = msg({message: 'AI chat'});
const ROLES_SECTION_TITLE_DESCRIPTOR = msg({
	message: 'Companion roles',
	comment: 'Heading of the role picker on the AI chat page in Discovery.',
});
const ROLES_SECTION_DESC_DESCRIPTOR = msg({
	message: 'Pick the default role for your next AI chat session.',
	comment: 'Description under the role picker heading on the AI chat page.',
});
const DEFAULT_BADGE_DESCRIPTOR = msg({
	message: 'Default',
	comment: 'Badge on the role card that is currently the default.',
});
const SET_DEFAULT_DESCRIPTOR = msg({
	message: 'Set as default',
	comment: 'Button that makes a role the default for AI chat sessions.',
});
const START_DESCRIPTOR = msg({
	message: 'Start AI chat',
	comment: 'Primary button on the AI chat page that starts a voice session.',
});
const SESSION_IN_PROGRESS_DESCRIPTOR = msg({
	message: 'Session in progress',
	comment: 'Status shown on the AI chat page while a companion session is running.',
});
const OPEN_PANEL_DESCRIPTOR = msg({
	message: 'Open companion panel',
	comment: 'Button that opens the floating companion panel where the conversation lives.',
});
const VOICE_HINT_DESCRIPTOR = msg({
	message: 'Voice chat: after you start, the conversation continues in the companion panel.',
	comment: 'Hint under the start button on the AI chat page.',
});
const MEMORY_TITLE_DESCRIPTOR = msg({
	message: 'Memory',
	comment: 'Heading of the memory note on the AI chat page.',
});
const MEMORY_DESC_DESCRIPTOR = msg({
	message: 'Your recent sessions with the same role are remembered automatically.',
	comment: 'Note about automatic per-role memory on the AI chat page.',
});
const LOAD_ROLES_FAILED_DESCRIPTOR = msg({
	message: 'Unable to load roles',
	comment: 'Error title when the AI chat role list cannot be loaded.',
});
const EMPTY_ROLES_DESCRIPTOR = msg({
	message: 'No roles available yet.',
	comment: 'Empty state when the AI chat role list is empty.',
});
const LOADING_ROLES_DESCRIPTOR = msg({
	message: 'Loading roles…',
	comment: 'Loading hint for the AI chat role list.',
});
const VIEW_MEMORY_DESCRIPTOR = msg({
	message: 'View memories',
	comment: 'Button that loads the per-role memories shown on the AI chat page.',
});
const EMPTY_MEMORY_DESCRIPTOR = msg({
	message: 'No memories yet.',
	comment: 'Empty state for a role with no remembered sessions on the AI chat page.',
});
const CLEAR_MEMORY_DESCRIPTOR = msg({
	message: 'Clear',
	comment: 'Button that clears the memories of one role on the AI chat page.',
});
const GREETING_PREFIX_DESCRIPTOR = msg({
	message: 'Opens with: {greeting}',
	comment: 'Role card line quoting the opening line of a role. Preserve {greeting}.',
});

interface CompanionTalkCatalog {
	scenes: Array<CompanionCommands.CompanionScene>;
	defaultSceneId: string | null;
	isLoading: boolean;
	error: string | null;
}

interface CompanionMemoryGroup {
	sceneId: string;
	sceneName: string;
	entries: Array<CompanionCommands.CompanionMemoryEntry>;
}

export const DiscoveryCompanionTalkPage = observer(function DiscoveryCompanionTalkPage() {
	const {i18n} = useLingui();
	const [catalog, setCatalog] = useState<CompanionTalkCatalog>({
		scenes: [],
		defaultSceneId: null,
		isLoading: true,
		error: null,
	});
	const [previewSceneId, setPreviewSceneId] = useState<string | null>(null);
	const [isStartSubmitting, setIsStartSubmitting] = useState(false);
	const [memoryGroups, setMemoryGroups] = useState<Array<CompanionMemoryGroup> | null>(null);
	const [isMemoryLoading, setIsMemoryLoading] = useState(false);

	const loadCatalog = useCallback(() => {
		setCatalog((current) => ({...current, isLoading: true, error: null}));
		void CompanionCommands.listScenes('talk')
			.then((response) => {
				setCatalog({
					scenes: response.scenes,
					defaultSceneId: response.default_scene_id ?? null,
					isLoading: false,
					error: null,
				});
			})
			.catch((cause: unknown) => {
				setCatalog((current) => ({
					...current,
					isLoading: false,
					error: CompanionCommands.getCompanionCommandErrorMessage(cause) ?? i18n._(LOAD_ROLES_FAILED_DESCRIPTOR),
				}));
			});
	}, [i18n]);

	useEffect(() => {
		loadCatalog();
	}, [loadCatalog]);

	// 页面打开时同步一次其他标签页/设备上的进行中会话。
	useEffect(() => {
		if (!CompanionSessionStore.hasActiveSession && !CompanionSessionStore.isStarting) {
			void CompanionSessionStore.recoverActiveSession();
		}
	}, []);

	const preferredSceneId = CompanionTalkPreferences.defaultSceneId;
	const effectiveDefaultSceneId =
		preferredSceneId && catalog.scenes.some((scene) => scene.id === preferredSceneId)
			? preferredSceneId
			: catalog.defaultSceneId;
	const hasActiveSession = CompanionSessionStore.hasActiveSession || CompanionSessionStore.isStarting;

	const handleStart = useCallback(() => {
		if (isStartSubmitting || hasActiveSession) return;
		setIsStartSubmitting(true);
		CompanionSessionStore.selectMode('talk');
		void CompanionSessionStore.start({kind: 'microphone'}, effectiveDefaultSceneId ?? undefined, 'talk')
			.catch(() => false)
			.finally(() => setIsStartSubmitting(false));
	}, [effectiveDefaultSceneId, hasActiveSession, isStartSubmitting]);

	const loadMemory = useCallback(() => {
		setIsMemoryLoading(true);
		void CompanionCommands.listTalkMemory()
			.then((response) => {
				const groups = new Map<string, CompanionMemoryGroup>();
				for (const entry of response.entries) {
					const group = groups.get(entry.scene_id) ?? {
						sceneId: entry.scene_id,
						sceneName: entry.scene_name || entry.scene_id,
						entries: [],
					};
					// 与服务端注入口径一致: 同角色最多取最近三条。
					if (group.entries.length < 3) group.entries.push(entry);
					groups.set(entry.scene_id, group);
				}
				setMemoryGroups([...groups.values()]);
			})
			.catch(() => {
				// 加载失败保持收起态以便重试, 不误显示为空。
				setMemoryGroups(null);
			})
			.finally(() => setIsMemoryLoading(false));
	}, []);

	const clearMemory = useCallback((sceneId: string) => {
		void CompanionCommands.clearTalkMemory(sceneId)
			.then(() => {
				setMemoryGroups(
					(current) => current?.map((group) => (group.sceneId === sceneId ? {...group, entries: []} : group)) ?? null,
				);
			})
			.catch(() => false);
	}, []);

	return (
		<div className={styles.container} data-flx="discovery.companion-talk.container">
			<header className={styles.header} data-flx="discovery.companion-talk.header">
				<div className={styles.headerContent} data-flx="discovery.companion-talk.header-content">
					{MobileLayout.enabled ? (
						<ChannelHeaderIcon
							icon={ArrowLeftIcon}
							iconWeight="bold"
							label={i18n._(GO_BACK_DESCRIPTOR)}
							onClick={() => Navigation.navigateToDiscover()}
							data-flx="discovery.companion-talk.button.back-to-discovery"
						/>
					) : null}
					<ChatsCircleIcon
						className={styles.headerIcon}
						weight="fill"
						aria-hidden
						data-flx="discovery.companion-talk.icon"
					/>
					<h1 className={styles.title} data-flx="discovery.companion-talk.title">
						{i18n._(AI_CHAT_TITLE_DESCRIPTOR)}
					</h1>
				</div>
			</header>
			<Scroller className={styles.scroller} data-flx="discovery.companion-talk.scroller">
				<div className={styles.content} data-flx="discovery.companion-talk.content">
					<section className={styles.section} data-flx="discovery.companion-talk.roles-section">
						<h2 className={styles.sectionTitle} data-flx="discovery.companion-talk.roles-title">
							{i18n._(ROLES_SECTION_TITLE_DESCRIPTOR)}
						</h2>
						<p className={styles.sectionDesc} data-flx="discovery.companion-talk.roles-desc">
							{i18n._(ROLES_SECTION_DESC_DESCRIPTOR)}
						</p>
						{catalog.isLoading && catalog.scenes.length === 0 ? (
							<div className={styles.hint} data-flx="discovery.companion-talk.roles-loading">
								{i18n._(LOADING_ROLES_DESCRIPTOR)}
							</div>
						) : catalog.error && catalog.scenes.length === 0 ? (
							<div className={styles.errorBanner} role="alert" data-flx="discovery.companion-talk.roles-error">
								<span data-flx="discovery.companion-talk.roles-error-text">{catalog.error}</span>
								<Button
									variant="secondary"
									small
									fitContent
									onClick={() => loadCatalog()}
									data-flx="discovery.companion-talk.button.retry-roles"
								>
									{i18n._(TRY_AGAIN_DESCRIPTOR)}
								</Button>
							</div>
						) : catalog.scenes.length === 0 ? (
							<div className={styles.hint} data-flx="discovery.companion-talk.roles-empty">
								{i18n._(EMPTY_ROLES_DESCRIPTOR)}
							</div>
						) : (
							<div className={styles.roleList} data-flx="discovery.companion-talk.role-list">
								{catalog.scenes.map((scene) => {
									const isDefault = scene.id === effectiveDefaultSceneId;
									const isPreviewing = previewSceneId === scene.id;
									return (
										<article
											key={scene.id}
											className={isDefault ? styles.roleCardDefault : styles.roleCard}
											aria-labelledby={`talk-role-${scene.id}`}
											data-flx="discovery.companion-talk.role-card"
										>
											<div className={styles.roleHead} data-flx="discovery.companion-talk.role-head">
												<span className={styles.roleIcon} data-flx="discovery.companion-talk.role-icon">
													<ChatsCircleIcon
														size={20}
														aria-hidden="true"
														data-flx="discovery.discovery.discovery-companion-talk-page.chats-circle-icon"
													/>
												</span>
												<h3
													id={`talk-role-${scene.id}`}
													className={styles.roleName}
													data-flx="discovery.companion-talk.role-name"
												>
													{scene.name}
												</h3>
												{isDefault ? (
													<span className={styles.defaultBadge} data-flx="discovery.companion-talk.default-badge">
														{i18n._(DEFAULT_BADGE_DESCRIPTOR)}
													</span>
												) : null}
											</div>
											{scene.description ? (
												<p className={styles.roleDesc} data-flx="discovery.companion-talk.role-desc">
													{scene.description}
												</p>
											) : null}
											{scene.greeting ? (
												<p className={styles.roleGreeting} data-flx="discovery.companion-talk.role-greeting">
													{i18n._(GREETING_PREFIX_DESCRIPTOR, {greeting: scene.greeting})}
												</p>
											) : null}
											<div className={styles.roleActions} data-flx="discovery.companion-talk.role-actions">
												{isPreviewing ? (
													<CompanionVoicePreview
														sceneId={scene.id}
														disabled={hasActiveSession}
														autoStart
														data-flx="discovery.discovery.discovery-companion-talk-page.companion-voice-preview"
													/>
												) : (
													<Button
														variant="secondary"
														small
														fitContent
														onClick={() => setPreviewSceneId(scene.id)}
														data-flx="discovery.companion-talk.button.preview"
													>
														{i18n._(msg({message: 'Preview voice'}))}
													</Button>
												)}
												<Button
													variant={isDefault ? 'ghost' : 'secondary'}
													small
													fitContent
													disabled={isDefault}
													onClick={() => CompanionTalkPreferences.setDefaultScene(scene.id)}
													data-flx="discovery.companion-talk.button.set-default"
												>
													{isDefault ? i18n._(DEFAULT_BADGE_DESCRIPTOR) : i18n._(SET_DEFAULT_DESCRIPTOR)}
												</Button>
											</div>
										</article>
									);
								})}
							</div>
						)}
					</section>

					<section className={styles.section} data-flx="discovery.companion-talk.start-section">
						{hasActiveSession ? (
							<>
								<h2 className={styles.sectionTitle} data-flx="discovery.companion-talk.active-title">
									{i18n._(SESSION_IN_PROGRESS_DESCRIPTOR)}
								</h2>
								<Button
									variant="primary"
									fitContent
									onClick={CompanionSessionStore.openPanel}
									data-flx="discovery.companion-talk.button.open-panel"
								>
									{i18n._(OPEN_PANEL_DESCRIPTOR)}
								</Button>
							</>
						) : (
							<>
								<Button
									variant="primary"
									fitContent
									disabled={catalog.scenes.length === 0}
									aria-busy={isStartSubmitting || CompanionSessionStore.isStarting}
									onClick={handleStart}
									data-flx="discovery.companion-talk.button.start"
								>
									{i18n._(START_DESCRIPTOR)}
								</Button>
								<p className={styles.hint} data-flx="discovery.companion-talk.voice-hint">
									{i18n._(VOICE_HINT_DESCRIPTOR)}
								</p>
							</>
						)}
					</section>

					<section className={styles.section} data-flx="discovery.companion-talk.memory-section">
						<h2 className={styles.sectionTitle} data-flx="discovery.companion-talk.memory-title">
							{i18n._(MEMORY_TITLE_DESCRIPTOR)}
						</h2>
						<p className={styles.sectionDesc} data-flx="discovery.companion-talk.memory-desc">
							{i18n._(MEMORY_DESC_DESCRIPTOR)}
						</p>
						{memoryGroups === null ? (
							<Button
								variant="secondary"
								small
								fitContent
								disabled={isMemoryLoading}
								onClick={() => void loadMemory()}
								data-flx="discovery.companion-talk.button.view-memories"
							>
								{i18n._(VIEW_MEMORY_DESCRIPTOR)}
							</Button>
						) : memoryGroups.length === 0 ? (
							<p className={styles.hint} data-flx="discovery.companion-talk.memory-empty">
								{i18n._(EMPTY_MEMORY_DESCRIPTOR)}
							</p>
						) : (
							<div className={styles.memoryList} data-flx="discovery.companion-talk.memory-list">
								{memoryGroups.map((group) => (
									<article
										key={group.sceneId}
										className={styles.memoryCard}
										data-flx="discovery.companion-talk.memory-card"
									>
										<div className={styles.memoryHead} data-flx="discovery.companion-talk.memory-head">
											<h3 className={styles.memoryRole} data-flx="discovery.companion-talk.memory-role">
												{group.sceneName}
											</h3>
											<Button
												variant="ghost"
												small
												fitContent
												onClick={() => void clearMemory(group.sceneId)}
												data-flx="discovery.companion-talk.button.clear-memory"
											>
												{i18n._(CLEAR_MEMORY_DESCRIPTOR)}
											</Button>
										</div>
										{group.entries.length === 0 ? (
											<p className={styles.hint} data-flx="discovery.companion-talk.memory-cleared">
												{i18n._(EMPTY_MEMORY_DESCRIPTOR)}
											</p>
										) : (
											<ul className={styles.memoryEntries} data-flx="discovery.companion-talk.memory-entries">
												{group.entries.map((entry) => (
													<li key={entry.session_id} data-flx="discovery.companion-talk.memory-entry">
														<span className={styles.memoryTime} data-flx="discovery.companion-talk.memory-time">
															{getFormattedCompactDateTime(new Date(entry.started_at))}
														</span>
														<p className={styles.memorySummary} data-flx="discovery.companion-talk.memory-summary">
															{entry.summary}
														</p>
													</li>
												))}
											</ul>
										)}
									</article>
								))}
							</div>
						)}
					</section>
				</div>
			</Scroller>
		</div>
	);
});
